/**
 * seoHelp — контент контекстной справки SEO-раздела (§9.5 ТЗ). Ключ = id экрана.
 * Три пункта: зачем · какие задачи · как пользоваться (+ мини-глоссарий).
 * Версионируется с кодом; при желании можно переопределять из БД (documents
 * kind='seo_help') — механику оставляем расширяемой.
 */

export interface HelpContent {
  title: string;
  why: string;
  tasks: string[];
  how: string[];
  glossary?: { term: string; def: string }[];
}

export const SEO_HELP: Record<string, HelpContent> = {
  overview: {
    title: 'SEO — Обзор',
    why: 'Одна главная цифра: органика → MQL и вклад в пайплайн, рядом опережающие метрики. Здесь видно, растёт ли органика в дисциплине рекламы (CPL ≤ 15 000 ₽).',
    tasks: [
      'Понять сколько органических MQL и по какой цене',
      'Увидеть сколько денежных кластеров ещё без страниц (гэпы)',
      'Взять топ-5 задач в работу',
    ],
    how: [
      'Смотрите KPI сверху — MQL, гэпы, задачи.',
      'Проваливайтесь в «Семантика/карта» за гэпами или в «Задачи» за бэклогом.',
    ],
    glossary: [
      { term: 'MQL', def: 'квалифицированный лид — запрос демо (форма + звонок)' },
      { term: 'гэп', def: 'денежный кластер, под который ещё нет страницы' },
    ],
  },
  'keyword-map': {
    title: 'Семантика и карта страниц',
    why: 'Показывает, по каким коммерческим запросам мы можем получать органические заявки, и какие из них ещё не закрыты страницей. Это отправная точка всего SEO — план работ рождается здесь.',
    tasks: [
      'Собрать BOFU-ядро из seed-фраз и конкурентов',
      'Увидеть гэпы (кластеры без страницы)',
      'Расставить приоритеты',
      'Отправить кластер в брифы/задачи',
    ],
    how: [
      'Нажмите «Собрать ядро» или добавьте кластеры вручную.',
      'Откройте вкладку «Гэпы» — это ваш список работ по приоритету.',
      'У нужного кластера нажмите «В задачи».',
    ],
    glossary: [
      { term: 'BOFU', def: 'запросы нижней части воронки — готовы покупать' },
      { term: 'каннибализация', def: 'две страницы под один SERP (плохо)' },
    ],
  },
  audits: {
    title: 'Аудиты — коммерческие факторы Яндекса',
    why: 'Коммерческие факторы дают ≈60–70% веса на транзакционных запросах Яндекса — рычаг №1 под заявки. Аудит даёт скор, veto-гейты и находки → задачи.',
    tasks: [
      'Проверить страницу на цены/реквизиты/контакты (veto)',
      'Оценить позитивные сигналы KOMMA (реестр Минцифры, 152-ФЗ…)',
      'Отправить находки в бэклог',
    ],
    how: [
      'Запустите коммерческий аудит по URL (через AI-чат со скиллом seo или вручную).',
      'Veto-гейт (нет цен/реквизитов/контактов) красит скор в красный — закройте его первым.',
    ],
    glossary: [
      { term: 'veto-гейт', def: 'критичная недостача, блокирующая вывод страницы' },
      { term: 'коммерческие факторы', def: 'сигналы доверия/коммерции для ранжирования в Яндексе' },
    ],
  },
  content: {
    title: 'Контент-хаб (план + брифы)',
    why: 'Конвейер производства страниц: тема из карты → бриф → статус → замер после публикации. Гейт качества не пускает переспам (Баден-Баден) в публикацию.',
    tasks: [
      'Вести страницы по статусам draft→review→published→live',
      'Генерить контент-брифы (с обязательным FAQ/HowTo под GEO)',
      'Проходить гейт качества текста перед публикацией',
    ],
    how: [
      'Создайте страницу, привяжите к кластеру.',
      'Сгенерируйте бриф, затем проверьте текст (гейт Тургенева).',
      'Переведите в «published» — при риске ≥ 8 нужен оверрайд роли seo/admin.',
    ],
    glossary: [
      { term: 'Баден-Баден', def: 'фильтр Яндекса за переспам текста' },
      { term: 'зрелая когорта', def: 'страницы старше 3 мес — по ним считаем прямой CPL' },
    ],
  },
  measure: {
    title: 'Органика → MQL',
    why: 'Доказывает ROI SEO в дисциплине рекламы: не трафик, а заявки при CPL ≤ 15 000 ₽. Считаем два показателя стоимости заявки.',
    tasks: [
      'Смотреть органические MQL (форма + звонок) по ПС',
      'Вести факты затрат по статьям (прямой CPL)',
      'Понимать какой тип страниц масштабировать',
    ],
    how: [
      'Введите фактические затраты за месяц (staff/content/dev/tools/other).',
      'Прямой CPL считается по зрелой когорте (страницы старше 3 мес).',
      'Бейдж «атрибуция частичная» — пока не внедрён проброс UTM в CRM.',
    ],
    glossary: [
      { term: 'прямой CPL', def: 'Σ затрат ÷ MQL зрелой когорты' },
      { term: 'MQL на усилие', def: 'MQL ÷ effort — что масштабировать' },
    ],
  },
  tasks: {
    title: 'Задачи (бэклог и апрувы)',
    why: 'Единый приоритизированный бэклог всех находок раздела. Каждая задача несёт доказательство, метку уверенности, impact×effort и поисковик.',
    tasks: [
      'Видеть все находки в одном месте по приоритету',
      'Апрувить задачи (роль seo/admin)',
      'Вести статусы new→approved→in_progress→done',
    ],
    how: [
      'Сортируйте по impact/effort, фильтруйте по модулю/ПС/статусу.',
      'Апрув меняет статус и пишет автора (апрув-контур как в рекламе).',
    ],
    glossary: [
      { term: 'impact×effort', def: 'ценность × трудозатраты (приоритизация)' },
      { term: 'уверенность', def: 'Confirmed / Likely / Hypothesis' },
    ],
  },
};
